/**
 * Centralized formatting utilities
 *
 * Issue #1341: Standardize duration and time display formats across the app.
 */

// =============================================================================
// Count Labels
// =============================================================================

/**
 * Format a count with its noun, singular only at exactly 1 (#3841).
 *
 * countLabel(1, 'member')  → "1 member"
 * countLabel(0, 'member')  → "0 members"
 * countLabel(2, 'reply', 'replies') → "2 replies"
 *
 * Pass `plural` for nouns that do not just take an "s".
 */
export function countLabel(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * RSVP summary for a Manage Events row (#3841). Zero counts are left off.
 *
 * rsvpSummaryLabel(1, 1) → "1 going, 1 maybe"
 * rsvpSummaryLabel(0, 1) → "1 maybe"
 * rsvpSummaryLabel(0, 0) → "No RSVPs yet"
 */
export function rsvpSummaryLabel(going: number, maybe: number): string {
  const parts: string[] = [];
  if (going > 0) parts.push(`${going} going`);
  if (maybe > 0) parts.push(`${maybe} maybe`);
  return parts.join(', ') || 'No RSVPs yet';
}

// =============================================================================
// Duration Formatting
// =============================================================================

/**
 * Format duration in minutes to human-readable string.
 *
 * Standard format:
 * - Under 1 hour: "45m"
 * - Exactly N hours: "2h"
 * - Hours + minutes: "1h 30m"
 *
 * NO decimals (use "1h 30m" not "1.5h")
 *
 * @param minutes Total minutes
 * @returns Formatted string like "1h 30m", "2h", "45m"
 */
export function formatDuration(minutes: number): string {
  if (minutes <= 0) return '0m';

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0 && mins > 0) {
    return `${hours}h ${mins}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${mins}m`;
  }
}

/**
 * Format duration with full words (for accessibility/screen readers).
 *
 * @param minutes Total minutes
 * @returns Formatted string like "1 hour 30 minutes", "2 hours", "45 minutes"
 */
export function formatDurationLong(minutes: number): string {
  if (minutes <= 0) return '0 minutes';

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  const hourStr = hours === 1 ? '1 hour' : `${hours} hours`;
  const minStr = mins === 1 ? '1 minute' : `${mins} minutes`;

  if (hours > 0 && mins > 0) {
    return `${hourStr} ${minStr}`;
  } else if (hours > 0) {
    return hourStr;
  } else {
    return minStr;
  }
}

// =============================================================================
// Relative Time Formatting
// =============================================================================

/**
 * Format a timestamp as relative time (time ago).
 *
 * Standard format:
 * - Under 1 minute: "just now"
 * - Under 1 hour: "5m ago"
 * - Under 24 hours: "3h ago"
 * - Under 7 days: "2d ago"
 * - Otherwise: "Jan 15"
 *
 * @param timestamp Unix timestamp in seconds
 * @returns Formatted string like "5m ago", "3h ago", "2d ago"
 */
export function formatTimeAgo(timestamp: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;

  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;

  // Older than a week - show date
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format relative time with full words (for accessibility).
 *
 * @param timestamp Unix timestamp in seconds
 * @returns Formatted string like "5 minutes ago", "3 hours ago"
 */
export function formatTimeAgoLong(timestamp: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;

  if (diff < 60) return 'just now';

  if (diff < 3600) {
    const mins = Math.floor(diff / 60);
    return `${mins} ${mins === 1 ? 'minute' : 'minutes'} ago`;
  }

  if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }

  if (diff < 604800) {
    const days = Math.floor(diff / 86400);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }

  // Older than a week - show date
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
